import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Target, Check } from 'lucide-react';

interface GoalOption {
  id: string;
  label: string;
  description: string;
  industries: string[];
}

const PREDICTION_GOALS: GoalOption[] = [
  {
    id: 'best_selling_products',
    label: 'Best-Selling Products',
    description: 'Identify which products generate the most revenue and units sold.',
    industries: ['ecommerce', 'logistics'],
  },
  {
    id: 'highest_revenue_period',
    label: 'Highest Revenue Period',
    description: 'Find peak sales periods to optimize inventory and marketing spend.',
    industries: ['ecommerce', 'finance', 'realestate'],
  },
  {
    id: 'demand_forecasting',
    label: 'Demand Forecasting',
    description: 'Predict future demand based on historical trends and seasonality.',
    industries: ['ecommerce', 'logistics', 'tech'],
  },
  {
    id: 'delivery_performance',
    label: 'Delivery & Operations Performance',
    description: 'Analyze on-time delivery rates, transit times, and operational bottlenecks.',
    industries: ['logistics'],
  },
  {
    id: 'customer_churn',
    label: 'Customer Churn Prediction',
    description: 'Identify customers at risk of leaving to enable proactive retention.',
    industries: ['tech', 'ecommerce', 'finance'],
  },
  {
    id: 'revenue_growth',
    label: 'Revenue Growth Trajectory',
    description: 'Model revenue trends and project future growth scenarios.',
    industries: ['finance', 'tech', 'ecommerce', 'realestate'],
  },
  {
    id: 'lead_conversion',
    label: 'Lead Conversion Optimization',
    description: 'Score leads and predict which prospects are most likely to convert.',
    industries: ['realestate', 'tech', 'finance'],
  },
  {
    id: 'cost_optimization',
    label: 'Cost & Expense Optimization',
    description: 'Find areas to reduce costs without impacting performance.',
    industries: ['logistics', 'finance', 'healthcare'],
  },
  {
    id: 'market_trends',
    label: 'Market Trend Analysis',
    description: 'Detect emerging trends and shifts in market behavior.',
    industries: ['realestate', 'finance', 'ecommerce'],
  },
  {
    id: 'risk_assessment',
    label: 'Risk Assessment',
    description: 'Evaluate financial or operational risks based on historical patterns.',
    industries: ['finance', 'healthcare', 'realestate'],
  },
];

interface Props {
  selectedGoals: string[];
  industry: string | null;
  onUpdate: (goals: string[]) => void;
}

export const PredictionGoalsStep = ({ selectedGoals, industry, onUpdate }: Props) => {
  const relevantGoals = industry
    ? PREDICTION_GOALS.filter(g => g.industries.includes(industry))
    : PREDICTION_GOALS;

  const otherGoals = industry
    ? PREDICTION_GOALS.filter(g => !g.industries.includes(industry))
    : [];

  const toggleGoal = (id: string) => {
    onUpdate(
      selectedGoals.includes(id)
        ? selectedGoals.filter(g => g !== id)
        : [...selectedGoals, id]
    );
  };

  const renderGoal = (goal: GoalOption) => (
    <Card
      key={goal.id}
      className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${
        selectedGoals.includes(goal.id) ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''
      }`}
      onClick={() => toggleGoal(goal.id)}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={selectedGoals.includes(goal.id)}
          onCheckedChange={() => toggleGoal(goal.id)}
          className="mt-0.5"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">{goal.label}</p>
            {selectedGoals.includes(goal.id) && <Check className="h-3.5 w-3.5 text-primary" />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{goal.description}</p>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">What Do You Want to Predict?</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Select the business questions you want answered. The AI will tailor its models to answer exactly these.
        </p>
        {selectedGoals.length > 0 && (
          <Badge variant="secondary" className="mb-3">
            {selectedGoals.length} goal{selectedGoals.length > 1 ? 's' : ''} selected
          </Badge>
        )}
      </div>

      {industry && (
        <div>
          <h4 className="text-sm font-medium mb-2 text-muted-foreground">
            Recommended for your industry
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {relevantGoals.map(renderGoal)}
          </div>
        </div>
      )}

      {otherGoals.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2 text-muted-foreground">Other available goals</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {otherGoals.map(renderGoal)}
          </div>
        </div>
      )}

      {!industry && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PREDICTION_GOALS.map(renderGoal)}
        </div>
      )}
    </div>
  );
};
